/**
 * attentionSummary — the one line the AttentionStrip shows when it is closed.
 *
 * D-3 replaces four stacked attention regions on the lead page (escalation
 * banner, draft dock, scheduled-messages bar, composer warning lines) with a
 * single strip between the thread and the composer. Closed, that strip is one
 * row: an icon, and the things waiting joined with ` · ` —
 * `⚠ 1 flag to review · agent drafting · 2 scheduled`.
 *
 * The wording, the ORDER, and the severity that tints the row are decided here
 * rather than in the component, because they are the part with rules worth
 * testing: which segments appear, that a lone flag reads "1 flag" and two read
 * "2 flags", that a severe flag colors the strip red while an ordinary one is
 * amber, and which section the strip opens to on the first click. The component
 * renders whatever this returns.
 *
 * Priority is the same top-to-bottom everywhere: flags a human must handle,
 * then what the agent is doing, then what is merely queued to send later.
 */

/** The strip's three expandable sections, in priority order. */
export type AttentionSection = 'flags' | 'drafts' | 'scheduled';

export interface AttentionSummaryInput {
  /** Raw thread escalations — filtered here by the rules below. */
  escalations: Array<{ kind: string; status: string }>;
  /** Pending agent turns awaiting a human (drafted + guardrail-suppressed). */
  draftCount: number;
  /** The agent is mid-generation (`draftingSince` is set). */
  drafting: boolean;
  /** Approved replies that have not gone out yet. */
  inFlightCount: number;
  /** Queued scheduled sends (reported up by ScheduledMessagesBar). */
  scheduledCount: number;
}

export interface AttentionSummary {
  /** Ordered, already-worded segments, e.g. ['1 flag to review','2 scheduled']. */
  segments: string[];
  /** Tints the collapsed row and picks its icon. 'none' = nothing to show. */
  severity: 'severe' | 'warn' | 'agent' | 'quiet' | 'none';
  /** The section the strip opens to on first expand (highest-priority non-empty). */
  openSection: AttentionSection | null;
}

/**
 * Ops/lifecycle kinds that are not a "concern about this lead" a recruiter acts
 * on from the thread. THE canonical list: EscalationBanner (the toggle-off
 * surface) imports this same set, so v1 and v2 can never show different flag
 * lists — the seam-2 verifier flagged the copied-in-two-places version.
 */
export const ATTENTION_EXCLUDE_KINDS = new Set(['draft_wait', 'dormant_handback']);

/**
 * The flags that read red instead of amber. Canonical for the same reason —
 * EscalationBanner consumes this set too.
 */
export const ATTENTION_SEVERE_KINDS = new Set(['hostile_or_distress', 'human_requested']);

/**
 * The escalations the lead page shows: still open or claimed by someone, and
 * not one of the excluded ops kinds. Generic so the strip can filter its real
 * `AgentEscalationLike[]` through the same one rule the summary counts with.
 */
export function visibleAttentionEscalations<T extends { kind: string; status: string }>(list: T[]): T[] {
  return list.filter(
    (e) => (e.status === 'open' || e.status === 'claimed') && !ATTENTION_EXCLUDE_KINDS.has(e.kind),
  );
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The closed strip's contents for one lead's current state. */
export function buildAttentionSummary(input: AttentionSummaryInput): AttentionSummary {
  const flags = visibleAttentionEscalations(input.escalations);
  const draftCount = Math.max(0, input.draftCount);
  const inFlightCount = Math.max(0, input.inFlightCount);
  const scheduledCount = Math.max(0, input.scheduledCount);

  const segments: string[] = [];
  if (flags.length) segments.push(`${plural(flags.length, 'flag')} to review`);
  // The count of written drafts leads the agent's own activity: a reply already
  // waiting for approval is a decision the recruiter owes, while "drafting" is
  // just a reason not to start typing.
  if (draftCount > 0) segments.push(`${plural(draftCount, 'draft')} waiting`);
  if (input.drafting) segments.push('agent drafting');
  if (inFlightCount > 0) segments.push(`${inFlightCount} sending soon`);
  if (scheduledCount > 0) segments.push(`${scheduledCount} scheduled`);

  /** Anything the agent is doing on this thread right now. */
  const agentBusy = draftCount > 0 || input.drafting || inFlightCount > 0;

  const severity: AttentionSummary['severity'] = flags.length
    ? (flags.some((e) => ATTENTION_SEVERE_KINDS.has(e.kind)) ? 'severe' : 'warn')
    : agentBusy
      ? 'agent'
      : scheduledCount > 0
        ? 'quiet'
        : 'none';

  const openSection: AttentionSection | null = flags.length
    ? 'flags'
    : agentBusy
      ? 'drafts'
      : scheduledCount > 0
        ? 'scheduled'
        : null;

  return { segments, severity, openSection };
}
